<template>
  <el-dialog
    :visible="visible"
    @close="$emit('cancelled')"
    :title="reward ? $t('loyalty.button.edit_reward') : $t('loyalty.button.add_reward')"
    width="900px"
    append-to-body
    custom-class="reward-form-dialog">
    <div class="px-6 py-4">
      <el-form :model="form" :rules="rules" ref="rewardForm" label-width="200px" class="reward-form">
        <!-- Basic Info -->
        <el-form-item :label="$t('loyalty.label.reward_name')" prop="name" class="mb-6">
          <el-input
            v-model="form.name"
            maxlength="255"
            :placeholder="$t('loyalty.placeholder.reward_name')"
            class="w-full">
          </el-input>
        </el-form-item>

        <el-form-item :label="$t('loyalty.label.reward_description')" prop="description" class="mb-6">
          <el-input
            type="textarea"
            v-model="form.description"
            :rows="3"
            :placeholder="$t('loyalty.placeholder.reward_description')"
            class="w-full">
          </el-input>
        </el-form-item>

        <!-- Reward Type and Points -->
        <div class="grid grid-cols-2 gap-6 mb-6">
          <el-form-item :label="$t('loyalty.label.reward_type')" prop="reward_type">
            <el-select v-model="form.reward_type" style="width: 100%" @change="handleRewardTypeChange">
              <el-option :label="$t('loyalty.reward_type.discount')" value="discount"></el-option>
              <el-option :label="$t('loyalty.reward_type.item')" value="item"></el-option>
              <el-option :label="$t('loyalty.reward_type.cash')" value="cash"></el-option>
              <el-option :label="$t('loyalty.reward_type.voucher')" value="voucher"></el-option>
            </el-select>
          </el-form-item>
          <el-form-item :label="$t('loyalty.label.points_required')" prop="points_required">
            <el-input-number
              v-model="form.points_required"
              :min="1"
              class="w-full">
            </el-input-number>
          </el-form-item>
        </div>

        <!-- Discount Fields -->
        <div v-if="form.reward_type === 'discount'" class="grid grid-cols-2 gap-6 mb-6">
          <el-form-item :label="$t('loyalty.label.discount_percentage')" prop="discount_percentage">
            <el-input-number
              v-model="form.discount_percentage"
              :min="0"
              :max="100"
              :precision="2"
              class="w-full">
            </el-input-number>
          </el-form-item>
          <el-form-item :label="$t('loyalty.label.discount_amount')" prop="discount_amount">
            <el-input-number
              v-model="form.discount_amount"
              :min="0"
              :precision="2"
              class="w-full">
            </el-input-number>
          </el-form-item>
        </div>

        <!-- Item Fields -->
        <div v-if="form.reward_type === 'item'" class="grid grid-cols-2 gap-6 mb-6">
          <el-form-item :label="$t('loyalty.label.item')" prop="item_id">
            <el-select
              v-model="form.item_id"
              filterable
              remote
              :remote-method="searchItems"
              :loading="itemsLoading"
              placeholder="Search for item"
              style="width: 100%">
              <el-option
                v-for="item in items"
                :key="item.id"
                :label="item.name"
                :value="item.id">
              </el-option>
            </el-select>
          </el-form-item>
          <el-form-item :label="$t('loyalty.label.item_quantity')" prop="item_quantity">
            <el-input-number
              v-model="form.item_quantity"
              :min="1"
              class="w-full">
            </el-input-number>
          </el-form-item>
        </div>

        <!-- Voucher Field -->
        <el-form-item
          v-if="form.reward_type === 'voucher'"
          :label="$t('loyalty.label.voucher_code')"
          prop="voucher_code"
          class="mb-6">
          <el-input
            v-model="form.voucher_code"
            maxlength="255"
            :placeholder="$t('loyalty.placeholder.voucher_code')"
            class="w-full">
          </el-input>
        </el-form-item>

        <!-- Stock and Validity -->
        <div class="grid grid-cols-3 gap-6 mb-6">
          <el-form-item :label="$t('loyalty.label.stock_quantity')" prop="stock_quantity">
            <el-input-number
              v-model="form.stock_quantity"
              :min="0"
              class="w-full">
            </el-input-number>
            <p class="text-xs text-gray-500 mt-1">{{ $t('loyalty.placeholder.stock_quantity') }}</p>
          </el-form-item>
          <el-form-item :label="$t('loyalty.label.valid_from')" prop="valid_from">
            <el-date-picker
              v-model="form.valid_from"
              type="date"
              format="yyyy-MM-dd"
              value-format="yyyy-MM-dd"
              placeholder="Select date"
              style="width: 100%">
            </el-date-picker>
          </el-form-item>
          <el-form-item :label="$t('loyalty.label.valid_until')" prop="valid_until">
            <el-date-picker
              v-model="form.valid_until"
              type="date"
              format="yyyy-MM-dd"
              value-format="yyyy-MM-dd"
              placeholder="Select date"
              :picker-options="{ disabledDate: (time) => form.valid_from && time.getTime() < new Date(form.valid_from).getTime() }"
              style="width: 100%">
            </el-date-picker>
          </el-form-item>
        </div>

        <!-- Image Upload -->
        <el-form-item :label="$t('loyalty.label.image')" prop="image" class="mb-6">
          <el-upload
            class="avatar-uploader"
            :action="uploadAction"
            :show-file-list="false"
            :on-success="handleImageSuccess"
            :before-upload="beforeImageUpload"
            :headers="uploadHeaders">
            <img v-if="form.image" :src="form.image" class="avatar" style="width: 200px; height: 200px; object-fit: cover;">
            <i v-else class="el-icon-plus avatar-uploader-icon"></i>
          </el-upload>
          <p class="text-xs text-gray-500 mt-1">{{ $t('loyalty.placeholder.image') }}</p>
        </el-form-item>

        <!-- Active Status -->
        <el-form-item :label="$t('loyalty.label.is_active')" prop="is_active" class="mb-6">
          <el-switch v-model="form.is_active"></el-switch>
        </el-form-item>
      </el-form>
    </div>
    <div slot="footer" class="dialog-footer px-6 py-4 bg-gray-50 flex justify-end space-x-3">
      <button
        @click="$emit('cancelled')"
        class="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-500 transition-colors">
        {{ $t('button.cancel') }}
      </button>
      <button
        @click="save"
        :disabled="saving"
        class="px-4 py-2 bg-gradient-to-r from-blue-600 to-blue-700 text-white rounded-lg hover:from-blue-700 hover:to-blue-800 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed">
        <span v-if="saving" class="flex items-center">
          <svg class="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
            <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
            <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
          </svg>
          {{ $t('status.loading') }}
        </span>
        <span v-else>{{ $t('button.save') }}</span>
      </button>
    </div>
  </el-dialog>
</template>

<script>
import ErrorHandlingMixin from '@/mixins/ErrorHandlingMixin.js'

export default {
  name: 'RewardFormDialog',
  mixins: [ErrorHandlingMixin],
  props: {
    visible: {
      type: Boolean,
      default: false
    },
    reward: {
      type: Object,
      default: null
    },
    programId: {
      type: Number,
      required: true
    }
  },
  data () {
    return {
      saving: false,
      items: [],
      itemsLoading: false,
      form: {
        name: '',
        description: '',
        reward_type: 'discount',
        points_required: 0,
        discount_percentage: null,
        discount_amount: null,
        item_id: null,
        item_quantity: 1,
        voucher_code: null,
        stock_quantity: null,
        valid_from: null,
        valid_until: null,
        is_active: true,
        image: null,
        restrictions: {}
      }
    }
  },
  computed: {
    rules () {
      return {
        name: [
          { required: true, message: this.$t('loyalty.validation.reward_name_required'), trigger: 'blur' },
          { max: 255, message: 'Name cannot exceed 255 characters', trigger: 'blur' }
        ],
        reward_type: [
          { required: true, message: 'Reward type is required', trigger: 'blur' }
        ],
        points_required: [
          { required: true, message: this.$t('loyalty.validation.points_required_required'), trigger: 'blur' },
          { type: 'number', min: 1, message: this.$t('loyalty.validation.points_required_positive'), trigger: 'blur' }
        ],
        item_id: [
          { required: this.form.reward_type === 'item', message: 'Item is required for item rewards', trigger: 'change' }
        ],
        valid_until: [
          {
            validator: (rule, value, callback) => {
              if (value && this.form.valid_from && new Date(value) < new Date(this.form.valid_from)) {
                callback(new Error('Valid until must be after valid from'))
              } else {
                callback()
              }
            },
            trigger: 'change'
          }
        ]
      }
    },
    uploadAction () {
      return `${this.$http.defaults.baseURL}/upload/image`
    },
    uploadHeaders () {
      return {
        Authorization: `Bearer ${this.$store.getters['Auth/getToken']}`
      }
    }
  },
  watch: {
    reward: {
      immediate: true,
      handler (newVal) {
        if (newVal) {
          this.form = {
            name: newVal.name || '',
            description: newVal.description || '',
            reward_type: newVal.reward_type || 'discount',
            points_required: newVal.points_required || 0,
            discount_percentage: newVal.discount_percentage || null,
            discount_amount: newVal.discount_amount || null,
            item_id: newVal.item_id || null,
            item_quantity: newVal.item_quantity || 1,
            voucher_code: newVal.voucher_code || null,
            stock_quantity: newVal.stock_quantity !== undefined ? newVal.stock_quantity : null,
            valid_from: newVal.valid_from || null,
            valid_until: newVal.valid_until || null,
            is_active: newVal.is_active !== undefined ? newVal.is_active : true,
            image: newVal.image || null,
            restrictions: newVal.restrictions || {}
          }
          if (newVal.item_id) {
            this.loadItem(newVal.item_id)
          }
        } else {
          this.resetForm()
        }
      }
    },
    visible (newVal) {
      if (newVal && this.$refs.rewardForm) {
        this.$refs.rewardForm.clearValidate()
      }
    }
  },
  methods: {
    resetForm () {
      this.form = {
        name: '',
        description: '',
        reward_type: 'discount',
        points_required: 0,
        discount_percentage: null,
        discount_amount: null,
        item_id: null,
        item_quantity: 1,
        voucher_code: null,
        stock_quantity: null,
        valid_from: null,
        valid_until: null,
        is_active: true,
        image: null,
        restrictions: {}
      }
      this.items = []
    },
    handleRewardTypeChange () {
      // Reset type-specific fields when type changes
      if (this.form.reward_type !== 'discount') {
        this.form.discount_percentage = null
        this.form.discount_amount = null
      }
      if (this.form.reward_type !== 'item') {
        this.form.item_id = null
        this.form.item_quantity = 1
      }
      if (this.form.reward_type !== 'voucher') {
        this.form.voucher_code = null
      }
    },
    async searchItems (query) {
      if (!query) {
        this.items = []
        return
      }
      try {
        this.itemsLoading = true
        const response = await this.$itemService.search({ q: query, limit: 20 })
        this.items = response.data || []
      } catch (error) {
        console.error('Error searching items:', error)
      } finally {
        this.itemsLoading = false
      }
    },
    async loadItem (itemId) {
      try {
        const item = await this.$itemService.get(itemId)
        this.items = [item]
      } catch (error) {
        console.error('Error loading item:', error)
      }
    },
    handleImageSuccess (response) {
      if (response.status === 'success' && response.data) {
        this.form.image = response.data.url || response.data.path
      }
    },
    beforeImageUpload (file) {
      const isImage = file.type.startsWith('image/')
      const isLt2M = file.size / 1024 / 1024 < 2

      if (!isImage) {
        this.$message.error('Avatar picture must be an image!')
      }
      if (!isLt2M) {
        this.$message.error('Avatar picture size can not exceed 2MB!')
      }
      return isImage && isLt2M
    },
    async save () {
      this.$refs.rewardForm.validate(async (valid) => {
        if (!valid) return

        try {
          this.saving = true
          const rewardData = {
            ...this.form,
            loyalty_program_id: this.programId
          }
          // Remove null values for optional fields
          Object.keys(rewardData).forEach(key => {
            if (rewardData[key] === null || rewardData[key] === '') {
              delete rewardData[key]
            }
          })
          let result
          if (this.reward) {
            result = await this.$store.dispatch('loyalty/rewards/updateReward', {
              id: this.reward.id,
              data: rewardData
            })
            this.showSuccess(this.$t('loyalty.message.reward_updated'))
          } else {
            result = await this.$store.dispatch('loyalty/rewards/createReward', rewardData)
            this.showSuccess(this.$t('loyalty.message.reward_created'))
          }
          this.$emit('saved', result)
        } catch (error) {
          console.error('Error saving reward:', error)
          this.globalErrHandle(error)
        } finally {
          this.saving = false
        }
      })
    }
  }
}
</script>

<style scoped>
.reward-form-dialog ::v-deep .el-dialog__body {
  padding: 0;
}

.reward-form ::v-deep .el-form-item__label {
  font-weight: 500;
  color: #374151;
}

.reward-form ::v-deep .el-input__inner,
.reward-form ::v-deep .el-textarea__inner {
  border-radius: 0.5rem;
  border-color: #d1d5db;
}

.reward-form ::v-deep .el-input__inner:focus,
.reward-form ::v-deep .el-textarea__inner:focus {
  border-color: #3b82f6;
}

.avatar-uploader ::v-deep .el-upload {
  border: 1px dashed #d9d9d9;
  border-radius: 6px;
  cursor: pointer;
  position: relative;
  overflow: hidden;
}

.avatar-uploader ::v-deep .el-upload:hover {
  border-color: #409EFF;
}

.avatar-uploader-icon {
  font-size: 28px;
  color: #8c8c8c;
  width: 200px;
  height: 200px;
  line-height: 200px;
  text-align: center;
}

.avatar {
  width: 200px;
  height: 200px;
  display: block;
}
</style>
